import { useCallback, useMemo, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight } from 'lucide-react';
import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import clsx from 'clsx';
import QueuePage from '../../components/ui/QueuePage';
import { useApp } from '../../hooks/useApp';

interface TransferRow {
  transfer_number?: string | null;
  isg_sku?: string | null;
  title?: string | null;
  status?: string | null;
  origin_location?: string | null;
  destination_location?: string | null;
  quantity?: number | null;
  return_number?: string | null;
  created_at?: string | null;
}

const STATUS_CLS: Record<string, string> = {
  pending: 'bg-surface-3 text-ink-secondary',
  in_transit: 'bg-info/15 text-info',
  received: 'bg-success/15 text-success',
  cancelled: 'bg-danger/15 text-danger',
};
const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  in_transit: 'In Transit',
  received: 'Received',
  cancelled: 'Cancelled',
};

function relativeTime(d: string | null | undefined): string {
  if (!d) return '\u2014';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function TransfersRemovals(): JSX.Element {
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer extends the existing dist_rep handling. Both
  // roles read-only on Removals.
  const { isDistRep, isViewer } = useApp();

  // Read-only treatment for dist_rep (Day 48 Phase 4c): the page already has
  // no bulk-action bar, no per-row mutation buttons, and no pricing columns.
  // The only defensive change is the SkuCell behavior — dist_rep is denied
  // on /catalog routes, so clicking a SKU would bounce them to /dashboard
  // via ProtectedRoute. Copy-to-clipboard is the read-only-correct action
  // here (matches Discrepancies + ReceivingDetail SkuCell pattern).
  const SkuCell = useCallback(
    (p: ICellRendererParams<TransferRow>): JSX.Element | null => {
      if (!p.data) return null;
      const sku = p.data.isg_sku;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!sku) return;
            if (isDistRep) {
              navigator.clipboard.writeText(sku);
            } else {
              navigate(`/catalog/products/${sku}`);
            }
          }}
          title={isDistRep ? 'Click to copy' : undefined}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {sku}
        </button>
      );
    },
    [navigate, isDistRep]
  );

  const StatusCell = useCallback((p: ICellRendererParams<TransferRow>): JSX.Element | null => {
    const status = p.data?.status;
    if (!status) return null;
    return (
      <span
        className={clsx(
          'px-1.5 py-0.5 rounded text-2xs font-medium',
          STATUS_CLS[status] || 'bg-surface-3 text-ink-muted'
        )}
      >
        {STATUS_LABEL[status] || status}
      </span>
    );
  }, []);

  const columns = useMemo<ColDef<TransferRow>[]>(
    () =>
      [
        {
          headerName: 'Transfer #',
          field: 'transfer_number',
          width: 130,
          pinned: 'left',
          suppressMovable: true,
          resizable: false,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
          },
        },
        {
          headerName: 'ISG SKU',
          field: 'isg_sku',
          width: 120,
          cellRenderer: SkuCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 180,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          cellRenderer: StatusCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Origin',
          field: 'origin_location',
          width: 120,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Destination',
          field: 'destination_location',
          width: 120,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Qty',
          field: 'quantity',
          width: 70,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Return #',
          field: 'return_number',
          width: 120,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
        {
          headerName: 'Created',
          field: 'created_at',
          width: 110,
          valueFormatter: (p: ValueFormatterParams<TransferRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
      ] as ColDef<TransferRow>[],
    [SkuCell, StatusCell]
  );

  return (
    <QueuePage
      title="Removals"
      rpcName="get_transfers_queue"
      rpcParams={{ p_type: 'removal' }}
      columns={columns}
      actionButton={
        <span className="text-2xs text-ink-muted px-2 py-0.5 rounded-md bg-surface-3 border border-border-subtle">
          Management coming post-launch
        </span>
      }
      statusTabs={[
        { key: 'all', label: 'All' },
        { key: 'pending', label: 'Pending' },
        { key: 'in_transit', label: 'In Transit' },
        { key: 'received', label: 'Received' },
      ]}
      emptyIcon={ArrowLeftRight}
      emptyMessage="No removals — removal transfers are created when returns are dispositioned as 'Return to Distributor'"
      searchPlaceholder="Search transfer #, SKU, or title..."
      readOnly={isViewer || isDistRep}
    />
  );
}
